'use client';

import Image from 'next/image';
import { useLanguage } from '@/context/LanguageContext';
import { Phone, Mail, MapPin } from 'lucide-react';

export default function Footer() {
  const { lang, t } = useLanguage();

  const serviceLinks = [
    { href: `/${lang}/services/conseil-gouvernance`, label: t('Conseil & Gouvernance IT', 'IT Advisory & Governance') },
    { href: `/${lang}/services/cybersecurite`, label: t('Cybersécurité', 'Cybersecurity') },
    { href: `/${lang}/services/infrastructure-reseau`, label: t('Infrastructure & Réseau', 'Infrastructure & Network') },
    { href: `/${lang}/services/support-helpdesk`, label: 'Support & Helpdesk' },
    { href: `/${lang}/services/supervision-continuite`, label: t('Supervision & Continuité', 'Supervision & Continuity') },
    { href: `/${lang}/services/sites-web-presence`, label: t('Sites Web & Présence', 'Websites & Presence') },
    { href: `/${lang}/services/outils-automatisation`, label: t('Outils & Automatisation', 'Tools & Automation') },
  ];

  const companyLinks = [
    { href: `/${lang}#services`, label: 'Services' },
    { href: `/${lang}#how-we-help`, label: t('Notre approche', 'Our approach') },
    { href: `/${lang}#faq`, label: 'FAQ' },
    { href: `/${lang}#contact`, label: 'Contact' },
    { href: `/${lang}/blog`, label: 'Blog' },
    { href: `/${lang}/brief`, label: t('Créer mon site web', 'Build my website') },
  ];

  return (
    <footer className="relative rounded-t-[2rem] border-t border-white/[0.06] bg-dark-800/50 overflow-hidden">
      {/* Subtle top glow */}
      <div className="pointer-events-none absolute left-1/2 top-0 h-[200px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-electric/[0.04] blur-[100px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Logo & description */}
          <div className="space-y-5">
            <div className="flex items-center gap-2.5">
              <Image
                src="/quorova_logo_mark_white_blue.png"
                alt=""
                width={32}
                height={32}
                className="rounded-lg"
              />
              <span className="font-display text-xl font-bold tracking-[0.15em] uppercase text-white">
                Quorova
              </span>
            </div>
            <p className="text-sm leading-relaxed text-white/50">
              {t(
                "De l\u2019infrastructure à la vitrine. Un seul partenaire de confiance pour tout votre numérique : infra, sécurité, cloud, support et présence web.",
                'From infrastructure to storefront. One trusted partner for all your digital needs: infra, security, cloud, support and web presence.'
              )}
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="mb-5 font-display text-xs font-semibold uppercase tracking-[0.2em] text-electric">
              Services
            </h3>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-sm text-white/50 transition-colors duration-300 hover:text-electric-bright"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="mb-5 font-display text-xs font-semibold uppercase tracking-[0.2em] text-electric">
              {t('Entreprise', 'Company')}
            </h3>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-sm text-white/50 transition-colors duration-300 hover:text-electric-bright"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="mb-5 font-display text-xs font-semibold uppercase tracking-[0.2em] text-electric">
              Contact
            </h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-sm">
                <Phone className="h-4 w-4 shrink-0 text-electric/60" />
                <a
                  href="tel:+33763406885"
                  className="text-white/50 transition-colors duration-300 hover:text-electric-bright"
                >
                  <span className="text-white/30 mr-1.5">FR</span>+33 7 63 40 68 85
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Phone className="h-4 w-4 shrink-0 text-electric/60" />
                <a
                  href="tel:+32491344499"
                  className="text-white/50 transition-colors duration-300 hover:text-electric-bright"
                >
                  <span className="text-white/30 mr-1.5">BE</span>+32 4 91 34 44 99
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 shrink-0 text-electric/60" />
                <a
                  href="mailto:contact@quorova.com"
                  className="text-white/50 transition-colors duration-300 hover:text-electric-bright"
                >
                  contact@quorova.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-electric/60" />
                <span className="text-white/50">
                  {t('France, Belgique, Luxembourg', 'France, Belgium, Luxembourg')}
                </span>
              </li>
            </ul>

            {/* CTA */}
            <a
              href={`/${lang}#contact`}
              className="mt-6 inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-electric/15 border border-electric/25 rounded-lg hover:bg-electric/25 hover:border-electric/40 transition-all duration-300"
            >
              {t('Demander un audit', 'Request an audit')}
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/[0.06] pt-8 text-sm text-white/50 sm:flex-row">
          <p>
            &copy; {new Date().getFullYear()} Quorova. {t('Tous droits réservés.', 'All rights reserved.')}
          </p>
          <a
            href="https://cogenta.io"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white/40 transition-colors duration-300"
          >
            <span className="text-xs">{t('Conçu par', 'Crafted by')}</span>
            <img src="/partners/cogenta.svg" alt="Cogenta" width={72} height={24} className="h-6 opacity-60 hover:opacity-80 transition-opacity" />
          </a>
        </div>
      </div>
    </footer>
  );
}
